

import React, { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { FileUpload, FileUploadProps, ItemTemplateOptions } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import UploadFileIcon from 'assets/upload-file.svg';

interface UploadFileProps extends FileUploadProps {
  width: string;
  height: string;
  square?: boolean;
  setCover: (file: File | null) => void;
  cover?: string | File | null;
  release?: any;
  hideTrashButton?: boolean;
}

export const UploadFile: React.FC<UploadFileProps> = (props) => {
  const {
    children,
    contentStyle,
    accept,
    maxFileSize,
    width,
    height,
    square,
    onSelect,
    setCover,
    cover,
    release,
    hideTrashButton,
    ...other
  } = props;

  const theme = useTheme();
  const uploadImageRef = useRef<FileUpload>(null);
  const toast = useRef<Toast>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const onFileInputClick = () => {
    const input = uploadImageRef.current?.getInput();
    if (input) {
      input.click();
    }
  };

  const isImageSizeValid = (file: File): Promise<boolean> => {
    const validFormats = ['image/png', 'image/jpeg'];
    if (!validFormats.includes(file.type)) {
      return Promise.resolve(false);
    }
  
    return new Promise((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
  
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        URL.revokeObjectURL(img.src);
  
        let valid = false;
  
        if (release?.release_type === "music_video") {
          const aspectRatio = (width / height).toFixed(2);
          valid =
            width >= 2560 &&
            height >= 1440 &&
            aspectRatio === (16 / 9).toFixed(2);
        } else {

          valid =true
        }
        resolve(valid);
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        resolve(false);
      };
    });
  };
  

  const handleSelect = async (event: any) => {
    const file = event.files?.[0];
    if (!file) return;

    const isValid = await isImageSizeValid(file);

    if (!isValid) {
      toast.current?.show({
        severity: 'error',
        summary: 'Invalid file',
        detail: release?.release_type === "music_video"
          ? 'Image must be PNG/JPEG, at least 2560x1440 pixels, and 16:9 ratio.'

          : 'Image must be PNG/JPEG.',
        life: 4000,
      });
      
      setObjectUrl(null)
      setCover(null);
      if (uploadImageRef.current) {
        (uploadImageRef.current as any).clear();
      }
      return;
    }

    setCover(file);
    if (onSelect) {
      onSelect(event);
    }
  };

  let imageSrc =
    typeof cover === 'string'
      ? cover
      : objectUrl;

  useEffect(() => {
    if (cover instanceof File) {
      const url = URL.createObjectURL(cover);
      setObjectUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      imageSrc = null;
      setObjectUrl(null);
    }
  }, [cover]);

  return (
    <Box>

      <Toast ref={toast} />
      <UploadFrame
        width={width}
        height={height}
        $square={!!square}
        onClick={onFileInputClick}
        role="button"
        aria-label="Upload cover image"
      >
        {/* Keep FileUpload always mounted so replacing image works reliably */}
        <HiddenFileUpload $interactive={!imageSrc}>
          <FileUpload
            ref={uploadImageRef}
            progressBarTemplate={<div />}
            headerStyle={{ display: 'none' }}
            contentStyle={{
              padding: 0,
              backgroundColor: 'transparent',
              borderRadius: '15px',
              cursor: 'pointer',
              width: '100%',
              height: '100%',
              ...contentStyle,
            }}
            // We render our own UI overlays (button / image), so keep templates empty.
            emptyTemplate={<div style={{ width: '100%', height: '100%' }} />}
            itemTemplate={(_file: object, _options: ItemTemplateOptions) => (
              <div style={{ width: '100%', height: '100%' }} />
            )}
            accept={accept || 'image/*,.jpg,.png'}
            maxFileSize={maxFileSize || 6000000}
            onSelect={handleSelect}
            {...other}
          />
        </HiddenFileUpload>

        {imageSrc ? (
          <>
            {!hideTrashButton && (
              <TrashButton
                onClick={(e) => {
                  e.stopPropagation();
                  setCover(null);
                }}
              >
                <i className="pi pi-trash"></i>
              </TrashButton>
            )}
            <CoverImage
              onClick={(e) => {
                e.stopPropagation();
                onFileInputClick();
              }}
              src={imageSrc}
              alt={cover instanceof File ? cover.name : 'cover'}
            />
          </>
        ) : (
          <CenterButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onFileInputClick();
            }}
          >
            <UploadFileIcon color={theme.palette.icon.primary} />
            {children}
          </CenterButton>
        )}
      </UploadFrame>
    </Box>
  );
};

const UploadFrame = styled.div<{ width: string; height: string; $square: boolean }>`
  position: relative;
  width: ${({ width }) => width};
  ${({ $square, height }) =>
    $square
      ? `
    aspect-ratio: 1 / 1;
    height: auto;
  `
      : `
    height: ${height};
  `}
  max-width: 100%;
  background-color: #e5e7eb; /* grey square when empty */
  border-radius: 15px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #d1d5db;
  }
`;

const HiddenFileUpload = styled.div<{ $interactive: boolean }>`
  position: absolute;
  inset: 0;
  opacity: 0;
  /* While image is shown, allow clicking trash / image (don't intercept). When empty, keep DnD working. */
  pointer-events: ${p => (p.$interactive ? 'auto' : 'none')};
  z-index: 1;
`;

const CenterButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 18px 20px;
  background-color: ${({ theme }) => theme.palette.background.primary};
  border-radius: 10px;
  border: none;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  position: relative;
  z-index: 2;
`;

const CoverImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  z-index: 2;
`;

const UploadImageViewContainer = styled.div<{ width: string; height: string }>`
  position: relative;
  width: ${({ width }) => width};
  height: ${({ height }) => height};
  padding: 0;
  border-radius: 10px;
  overflow: hidden;
  cursor: default;
`;

const TrashButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  width: 30px;
  position: absolute;
  top: 10px;
  right: 10px;
  border-radius: 4px;
  padding: 5px;
  cursor: pointer;
  background-color: ${({ theme }) => theme.palette.background.primary};
  z-index: 3;
`;

const Box = styled.div`
  width: 422px;
  max-width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;
